import type { FC } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Typography } from '@mui/material'
import { EventType } from '@/features/events/shared/types'
import { formatMcpToolForDisplay, formatToolNameForDisplay } from '@/features/events/shared/utils'

interface ToolHeaderProps {
  readonly eventType: EventType
  readonly toolName: string
}

export const ToolHeader: FC<ToolHeaderProps> = ({ eventType, toolName }) => {
  const headerText = getToolActionHeader(eventType, toolName)

  return (
    <Box sx={headerContainerSx}>
      <Typography variant="subtitle2" sx={headerTextSx}>
        {headerText}
      </Typography>
    </Box>
  )
}

function getToolActionHeader(eventType: EventType, toolName: string): string {
  if (eventType === EventType.TOOL_PERMISSION_REQUEST) {
    return getPermissionRequestHeader(toolName)
  }

  if (eventType === EventType.FILE_MODIFICATION) {
    return getPostToolUseHeader(toolName)
  }

  return toolName
}

function getPermissionRequestHeader(toolName: string): string {
  const headers: Record<string, string> = {
    Bash: 'Allow bash command use',
    Read: 'Allow read file',
    Write: 'Allow write file',
    Edit: 'Allow edit file',
    WebFetch: 'Allow web content fetch',
    WebSearch: 'Allow web search',
    Glob: 'Allow find files matching pattern',
    Grep: 'Allow search in files matching pattern',
  }

  if (headers[toolName]) {
    return headers[toolName]
  }

  const mcpFormatted = formatMcpToolForDisplay(toolName)
  if (mcpFormatted) {
    return `Allow ${mcpFormatted.toolDisplayName} ${mcpFormatted.actionDisplayName}`
  }

  return `Allow ${formatToolNameForDisplay(toolName)} use`
}

function getPostToolUseHeader(toolName: string): string {
  const headers: Record<string, string> = {
    Write: 'Created new file',
    Edit: 'Edited file',
  }

  if (headers[toolName]) {
    return headers[toolName]
  }

  const mcpFormatted = formatMcpToolForDisplay(toolName)
  if (mcpFormatted) {
    return `${mcpFormatted.toolDisplayName} ${mcpFormatted.actionDisplayName} completed`
  }

  return `${toolName} completed`
}

const headerContainerSx: SxProps<Theme> = {
  mb: 1.5,
}

const headerTextSx: SxProps<Theme> = {
  fontWeight: 600,
  fontSize: 'inherit',
}
